import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import { toAppError } from "./errors";

/**
 * The only way to reach the backend.
 *
 * One wrapper, so error normalisation happens in a single place and a raw
 * Firebase code never reaches a screen.
 */
export async function call<TRequest, TResponse>(
  name: string,
  data?: TRequest
): Promise<TResponse> {
  try {
    const result = await httpsCallable<TRequest, TResponse>(functions(), name)(data);
    return result.data;
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Every callable this console is allowed to reach.
 *
 * Deliberately short. An admin console with access to everything is an admin
 * console whose blast radius is everything — most of what this application does
 * is read Firestore under rules that already say `isSpontarAdmin()`, and the
 * callables are the handful of actions that change something.
 */
export const CALLABLES = {
  me: "me",
  createInstitution: "createInstitution",
  updateInstitution: "updateInstitution",
  setLicenceManually: "setLicenceManually",
  suspendInstitution: "suspendInstitution",
  exportInstitution: "exportInstitution",
  deleteInstitution: "deleteInstitution",
  publishLegalDocument: "publishLegalDocument",
  getLegalRegistry: "getLegalRegistry",
  getLegalMatrix: "getLegalMatrix",
  getLegalCoverage: "getLegalCoverage",
  previewDocumentSet: "previewDocumentSet",
  previewLegalBody: "previewLegalBody",
  getAiBoundaryReport: "getAiBoundaryReport",
  getAiCostReport: "getAiCostReport",
  getRetentionStatus: "getRetentionStatus",
  runRetentionForInstitution: "runRetentionForInstitution",
  getPlatformHealth: "getPlatformHealth",
  listMigrations: "listMigrations",
  runDataMigration: "runDataMigration",
  /*
    Deliberately absent: `getAiUsage`.

    It reports one institution's spend against its budget and is guarded as
    owner-only, so a Spontar admin — who belongs to no institution — is refused
    by it. The console's own view of what the platform spends is phase 5's, and
    it needs a callable that reads across tenants rather than this one.
  */
  changeUserRole: "changeUserRole",
  setUserDisabled: "setUserDisabled",
  resetUserPassword: "resetUserPassword",
  /*
    Sends a member of staff their welcome mail again, with a fresh
    password-setup link. The one way this console can put a key to an
    institution in front of somebody, which is why the backend takes it from
    a Spontar admin and nobody else.
  */
  sendSignInLink: "sendSignInLink",
  requestSupportAccess: "requestSupportAccess",
  setContactStatus: "setContactStatus",
  logSupportView: "logSupportView",
} as const;
